import { Box, Button, Chip, DialogContent, Divider, Stack, Typography } from "@mui/material";
import { useCallback } from "react";
import biApi from "../../../../../api/biApi.ts";
import DialogButtons from "@entrilia/shared/components/DialogButtons.tsx";
import LoaderBox from "@entrilia/shared/components/LoaderBox.tsx";
import PromptDialog from "@entrilia/shared/components/dialog/PromptDialog.tsx";
import PromptDialogHeader from "@entrilia/shared/components/dialog/PromptDialogHeader.tsx";
import useFetch from "@entrilia/shared/hooks/useFetch.ts";
import { convertISODate } from "@entrilia/shared/utilities/dateUtils.ts";
import getReportTypeIcon from "../../sv/components/common/visualization/icons/getReportTypeIcon.tsx";
import NewLabel from "../../../../common/NewLabel.tsx";
import TemplateInfoFields from "./TemplateInfoFields.tsx";
import { formatFundType } from "./helpers/reportTemplateHelper.ts";
import type { ReportTemplate } from "../../../../../api/types/svTypes.ts";

interface Props {
  template: ReportTemplate;
  onClose: () => void;
  onUseTemplate: (template: ReportTemplate) => void;
}

export default function TemplateInfoDialog({ template, onClose, onUseTemplate }: Props) {
  const getTemplateFieldsData = useCallback(() => {
    return biApi.getTemplateFieldsData({ templateId: template.reportId });
  }, [template.reportId]);

  const [data, , { isFetching }] = useFetch(getTemplateFieldsData);
  const fields = data?.fields || {};
  const isConfigurationsEmpty = Object.values(fields).every((f) => f.length === 0);

  return (
    <PromptDialog open sx={{ minWidth: 740, height: "100%", maxHeight: 780, p: 0 }}>
      <PromptDialogHeader text="Template" onClose={onClose} />
      <Divider orientation="horizontal" flexItem />
      <DialogContent sx={{ height: "100%" }}>
        <LoaderBox loading={isFetching}>
          <Stack sx={{ gap: 2 }}>
            <Stack id="template-info" sx={{ gap: 2.5 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="h5">{template.name}</Typography>
                  {template.isNew && <NewLabel />}
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {template.fundTypes.map((ft) => (
                    <Chip
                      key={ft}
                      label={formatFundType(ft)}
                      variant="outlined"
                      sx={{ py: 0.375, px: 0.5, fontSize: 13 }}
                    />
                  ))}
                </Box>
              </Box>
              {template.description.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography variant="subtitle2">Description</Typography>
                  <Typography>{template.description}</Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", gap: 3.5 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography variant="subtitle2">Report Type</Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Box sx={(theme) => ({ display: "inline-flex", color: theme.palette.action.active })}>
                      {getReportTypeIcon(template.reportType)}
                    </Box>
                    <Typography>{template.reportType}</Typography>
                  </Box>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, justifyContent: "space-between" }}>
                  <Typography variant="subtitle2">Added At</Typography>
                  <Typography>{convertISODate(template.createdAt)}</Typography>
                </Box>
              </Box>
            </Stack>
            <Divider orientation="horizontal" flexItem sx={{ borderStyle: "dashed" }} />
            {!isConfigurationsEmpty && <TemplateInfoFields fields={fields} />}
          </Stack>
        </LoaderBox>
      </DialogContent>
      <Divider orientation="horizontal" flexItem />
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" disabled={isFetching} onClick={() => onUseTemplate(template)}>
          Use Template
        </Button>
      </DialogButtons>
    </PromptDialog>
  );
}
